import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../components/layout/Layout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import apiClient from "../services/apiClient";
import { useProposal } from "../contexts/ProposalContext";

const HolisticGenerationPage = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const { currentProposal, fetchProposal } = useProposal();

  const [formData, setFormData] = useState({
    documentName: "",
    templateFile: null,
    customInstructions: "",
    selectedModel: "",
  });

  const [isDragging, setIsDragging] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [error, setError] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [isTemplateExpanded, setIsTemplateExpanded] = useState(false);
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [isModelContentExpanded, setIsModelContentExpanded] = useState(false);
  const [customPromptInstructions, setCustomPromptInstructions] = useState('');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Model content state
  const [modelContentDocuments, setModelContentDocuments] = useState([]);
  const [selectedModelContent, setSelectedModelContent] = useState([]);
  const [modelContentLoading, setModelContentLoading] = useState(false);

  const isGeneratingRef = useRef(false);

  // Default AI instructions
  const DEFAULT_PROMPT_INSTRUCTIONS = `IMPORTANT INSTRUCTIONS:
1. Write a COMPLETE, compelling proposal that demonstrates why our company is the best choice
2. Address ALL requirements and evaluation criteria from the RFP documents clearly and thoroughly
3. Highlight our company's strengths, discriminators, and unique value propositions
4. Emphasize benefits to the customer throughout the proposal
5. Use clear, active, and persuasive language that builds confidence
6. Base all claims on the context documents about our company and solutions
7. Follow the structure and notes in the template if provided
8. Mark sections clearly using this format: === SECTION: [Section Title] ===
9. For sections requiring specific personnel, past performance, or company data not in context documents: Create realistic DUMMY/TEMPLATE data that shows proper formatting, then add ACTION NEEDED prompt for replacement with actual data

YOUR TASK:

Create a WINNING proposal that compels the evaluators to select C2 by:

**PRIMARY FOCUS - Win Themes & Persuasion:**
- Lead every section with a discriminator that positions C2 as uniquely qualified
- Write in persuasive, active voice that speaks directly to the customer's needs
- Transform every requirement into a customer benefit, not a feature checklist
- Use specific details from the RFP to show you understand their environment
- Include proof points (metrics, examples, experience) wherever possible

**COMPLETENESS:**
- Address ALL requirements and evaluation criteria from the RFP
- Use AI placeholders (⚠️ ACTION NEEDED) when you lack specific information
- Flag sections where standard company materials should be inserted
- For Key Personnel, Past Performance, or similar sections: Create realistic dummy/template data if you lack specifics
- **BE STRATEGICALLY CONCISE**: Evaluators prefer clear, scannable proposals over exhaustive detail—lead with impact, support with key proof points, keep moving. You must complete ALL sections within token limits.

**STRUCTURE & QUALITY:**
- Begin each major section with a discriminator paragraph
- Use clear, scannable formatting with headers and lists
- Maintain professional, confident tone throughout`;

  // Load proposal data
  useEffect(() => {
    if (proposalId) {
      fetchProposal(parseInt(proposalId));
    }
  }, [proposalId, fetchProposal]);

  // Auto-populate document name when proposal loads
  useEffect(() => {
    if (currentProposal && !formData.documentName) {
      setFormData(prev => ({
        ...prev,
        documentName: `${currentProposal.proposal_name} - Proposal`
      }));
    }
  }, [currentProposal]);

  // Load data on mount
  useEffect(() => {
    loadAvailableModels();
    loadSavedTemplates();
    loadModelContentDocuments();
  }, []);

  const loadSavedTemplates = async () => {
    try {
      const response = await apiClient.getAIInstructionTemplates();
      if (response.success) {
        setSavedTemplates(response.data.templates || []);
        const defaultTemplate = response.data.templates.find(t => t.is_default === 1);
        if (defaultTemplate) {
          setSelectedTemplateId(defaultTemplate.template_id.toString());
          setCustomPromptInstructions(defaultTemplate.instructions);
        }
      }
    } catch (error) {
      console.error('Failed to load saved templates:', error);
    }
  };

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await apiClient.getAIModels();

      if (response.success && response.data.models) {
        setAvailableModels(response.data.models);

        const defaultModel = response.data.models.find(m => m.isDefault);
        if (defaultModel) {
          setFormData(prev => ({
            ...prev,
            selectedModel: defaultModel.model
          }));
        }
      }
      setModelsLoading(false);
    } catch (error) {
      console.error('Failed to load AI models:', error);
      setError('Failed to load AI models. Using default.');
      setModelsLoading(false);
    }
  };

  const loadModelContentDocuments = async () => {
    try {
      setModelContentLoading(true);
      const response = await apiClient.getModelContentDocuments();
      if (response.success) {
        // Filter only completed documents (not images)
        const docs = (response.data.documents || []).filter(
          doc => doc.processing_status === 'completed'
        );
        setModelContentDocuments(docs);
      }
    } catch (error) {
      console.error('Failed to load model content:', error);
    } finally {
      setModelContentLoading(false);
    }
  };

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);

    if (templateId) {
      const template = savedTemplates.find(t => t.template_id.toString() === templateId);
      if (template) {
        setCustomPromptInstructions(template.instructions);
      }
    } else {
      setCustomPromptInstructions('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedExtensions = ['.docx', '.pdf', '.doc'];
    const fileName = file.name.toLowerCase();
    const isValidType = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidType) {
      setError("Please upload a .docx, .pdf, or .doc file");
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError("File size must be less than 10MB");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      templateFile: file,
    }));
    setIsTemplateExpanded(true);
    setError(null);
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      templateFile: null,
    }));
  };

  const handleModelContentToggle = (documentId) => {
    setSelectedModelContent(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) {
      console.log("⚠️ Generation already in progress, skipping duplicate call");
      return;
    }

    if (!formData.documentName) {
      setError("Please enter a document name");
      return;
    }

    if (!formData.selectedModel) {
      setError("Please select an AI model");
      return;
    }

    isGeneratingRef.current = true;
    setIsGenerating(true);
    setError(null);
    setGenerationStatus({
      status: 'preparing',
      message: 'Preparing document generation...'
    });

    try {
      const uploadData = new FormData();
      uploadData.append('proposalId', proposalId);
      uploadData.append('documentName', formData.documentName);
      uploadData.append('customInstructions', formData.customInstructions || '');
      uploadData.append('selectedModel', formData.selectedModel || '');
      uploadData.append('customPromptInstructions', customPromptInstructions || '');

      // Add selected model content IDs
      if (selectedModelContent.length > 0) {
        uploadData.append('modelContentIds', JSON.stringify(selectedModelContent));
      }

      if (formData.templateFile) {
        uploadData.append('template', formData.templateFile);
      }

      setGenerationStatus({
        status: 'generating',
        message: 'AI is creating your complete proposal document... This may take 10-15 minutes.'
      });

      const response = await apiClient.client.post(
        '/api/editable-documents/holistic-generate',
        uploadData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const apiResult = response;

      if (!apiResult || !apiResult.success) {
        throw new Error(apiResult?.message || 'Failed to generate document');
      }

      setGenerationStatus({
        status: 'completed',
        message: 'Document created successfully!'
      });

      isGeneratingRef.current = false;

      // Navigate to the newly created document for editing
      setTimeout(() => {
        navigate(`/workspace/${proposalId}/document/${apiResult.data.documentId}`);
      }, 1500);

    } catch (error) {
      console.error("❌ Holistic generation failed:", error);
      setError(error.response?.data?.message || error.message || "Failed to generate document. Please try again.");
      setGenerationStatus(null);
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }
  };

  const handleBack = () => {
    if (!isGenerating) {
      navigate(`/workspace/${proposalId}/documents`);
    }
  };

  return (
    <Layout title={currentProposal ? `Create Proposal with AI - ${currentProposal.proposal_name}` : "Create Proposal with AI"}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                <FontAwesomeIcon icon="magic" className="mr-3 text-purple-600" />
                Create Proposal with AI
              </h1>
              {currentProposal ? (
                <p className="text-slate-600 mt-2">
                  Creating document for{" "}
                  <span className="font-medium">{currentProposal.proposal_name}</span>
                  {currentProposal.customer_name && (
                    <span className="text-slate-500">
                      {" "}• {currentProposal.customer_name}
                    </span>
                  )}
                  {currentProposal.rfp_number && (
                    <span className="text-slate-500">
                      {" "}({currentProposal.rfp_number})
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-slate-600 mt-2">
                  Generate a complete proposal document using AI-powered holistic generation
                </p>
              )}
            </div>
            {!isGenerating && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <FontAwesomeIcon icon="arrow-left" className="mr-2" />
                Back
              </button>
            )}
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="space-y-6">
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-red-500 mt-0.5" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {/* Document Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Document Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="documentName"
                value={formData.documentName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                disabled={isGenerating}
                required
              />
            </div>

            {/* AI Model Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                AI Model <span className="text-red-500">*</span>
              </label>
              {modelsLoading ? (
                <div className="flex items-center space-x-2 text-slate-500">
                  <LoadingSpinner size="sm" />
                  <span>Loading models...</span>
                </div>
              ) : (
                <select
                  name="selectedModel"
                  value={formData.selectedModel}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  disabled={isGenerating}
                  required
                >
                  <option value="">Select AI Model...</option>
                  {availableModels.filter(m => m.provider === 'openai').length > 0 && (
                    <optgroup label="OpenAI">
                      {availableModels
                        .filter(m => m.provider === 'openai')
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name} {model.isDefault && '(Default)'}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {availableModels.filter(m => m.provider === 'anthropic').length > 0 && (
                    <optgroup label="Anthropic">
                      {availableModels
                        .filter(m => m.provider === 'anthropic')
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name} {model.isDefault && '(Default)'}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {availableModels.filter(m => m.provider === 'google').length > 0 && (
                    <optgroup label="Google">
                      {availableModels
                        .filter(m => m.provider === 'google')
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name} {model.isDefault && '(Default)'}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
              )}
            </div>

            {/* Template Upload Section - Collapsible */}
            <div className="border border-slate-200 rounded-lg">
              <button
                type="button"
                onClick={() => setIsTemplateExpanded(!isTemplateExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                disabled={isGenerating}
              >
                <span className="flex items-center text-sm font-medium text-slate-700">
                  <FontAwesomeIcon icon="file-upload" className="mr-2 text-slate-400" />
                  Template Upload (Optional)
                </span>
                <FontAwesomeIcon
                  icon={isTemplateExpanded ? "chevron-up" : "chevron-down"}
                  className="text-slate-400"
                />
              </button>

              {isTemplateExpanded && (
                <div className="border-t border-slate-200 p-4">
                  {!formData.templateFile ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        isDragging
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-300 hover:border-purple-400'
                      }`}
                    >
                      <FontAwesomeIcon
                        icon="cloud-upload-alt"
                        className="text-4xl text-slate-400 mb-3"
                      />
                      <p className="text-sm text-slate-600 mb-2">
                        Drag and drop your template file here, or
                      </p>
                      <label className="inline-block px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 cursor-pointer transition-colors">
                        <input
                          type="file"
                          onChange={handleFileSelect}
                          accept=".docx,.pdf,.doc"
                          className="hidden"
                          disabled={isGenerating}
                        />
                        Browse Files
                      </label>
                      <p className="text-xs text-slate-500 mt-2">
                        Supported formats: .docx, .pdf, .doc (Max 10MB)
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <FontAwesomeIcon icon="file" className="text-purple-500" />
                        <span className="text-sm text-slate-700">{formData.templateFile.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="text-red-500 hover:text-red-700"
                        disabled={isGenerating}
                      >
                        <FontAwesomeIcon icon="times" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Model Content Section - Collapsible */}
            <div className="border border-slate-200 rounded-lg">
              <button
                type="button"
                onClick={() => setIsModelContentExpanded(!isModelContentExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                disabled={isGenerating}
              >
                <span className="flex items-center text-sm font-medium text-slate-700">
                  <FontAwesomeIcon icon="file-contract" className="mr-2 text-slate-400" />
                  Model Content Reference (Optional)
                  {selectedModelContent.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                      {selectedModelContent.length} selected
                    </span>
                  )}
                </span>
                <FontAwesomeIcon
                  icon={isModelContentExpanded ? "chevron-up" : "chevron-down"}
                  className="text-slate-400"
                />
              </button>

              {isModelContentExpanded && (
                <div className="border-t border-slate-200 p-4">
                  <p className="text-sm text-slate-600 mb-4">
                    Select reference documents to guide the AI during proposal generation
                  </p>

                  {modelContentLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner size="sm" text="Loading model content..." />
                    </div>
                  ) : modelContentDocuments.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FontAwesomeIcon icon="inbox" className="text-3xl mb-2" />
                      <p className="text-sm">No model content documents available</p>
                      <p className="text-xs mt-1">Upload model content in the Admin Panel</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {modelContentDocuments.map((doc) => (
                        <label
                          key={doc.document_id}
                          className="flex items-start p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-slate-200"
                        >
                          <input
                            type="checkbox"
                            checked={selectedModelContent.includes(doc.document_id)}
                            onChange={() => handleModelContentToggle(doc.document_id)}
                            className="mt-1 mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                            disabled={isGenerating}
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-slate-900">
                              {doc.document_name}
                            </div>
                            {doc.description && (
                              <div className="text-xs text-slate-500 mt-1 italic">
                                AI Instruction: {doc.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Advanced Options Section - Collapsible */}
            <div className="border border-slate-200 rounded-lg">
              <button
                type="button"
                onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                disabled={isGenerating}
              >
                <span className="flex items-center text-sm font-medium text-slate-700">
                  <FontAwesomeIcon icon="sliders-h" className="mr-2 text-slate-400" />
                  Advanced: Custom AI Prompt Instructions
                </span>
                <FontAwesomeIcon
                  icon={isAdvancedExpanded ? "chevron-up" : "chevron-down"}
                  className="text-slate-400"
                />
              </button>

              {isAdvancedExpanded && (
                <div className="border-t border-slate-200 p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Saved Instruction Templates
                    </label>
                    <select
                      value={selectedTemplateId}
                      onChange={handleTemplateSelect}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      disabled={isGenerating}
                    >
                      <option value="">Use System Default</option>
                      {savedTemplates.map((template) => (
                        <option key={template.template_id} value={template.template_id}>
                          {template.template_name}
                          {template.is_default === 1 && ' (Default)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Custom Prompt Instructions
                    </label>
                    <textarea
                      value={customPromptInstructions || DEFAULT_PROMPT_INSTRUCTIONS}
                      onChange={(e) => setCustomPromptInstructions(e.target.value)}
                      rows={10}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-xs"
                      disabled={isGenerating}
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      These instructions guide how the AI generates your proposal
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Instructions */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Custom Instructions <span className="text-slate-500 font-normal">(Optional)</span>
              </label>
              <textarea
                name="customInstructions"
                value={formData.customInstructions}
                onChange={handleInputChange}
                placeholder="Add any specific instructions for this document..."
                rows={3}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                disabled={isGenerating}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={handleBack}
                className="px-6 py-3 text-slate-600 hover:text-slate-900 transition-colors"
                disabled={isGenerating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || !formData.documentName || !formData.selectedModel}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <FontAwesomeIcon icon="magic" className="mr-2" />
                Generate Proposal
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Full-Screen Generation Modal */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-6">
                <LoadingSpinner size="lg" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {generationStatus?.status === 'completed' ? (
                  <>
                    <FontAwesomeIcon icon="check-circle" className="text-green-500 mr-2" />
                    Success!
                  </>
                ) : (
                  'Generating Proposal...'
                )}
              </h3>
              <p className="text-slate-600 mb-4">
                {generationStatus?.message || 'Please wait...'}
              </p>
              {generationStatus?.status === 'generating' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-blue-800">
                    <FontAwesomeIcon icon="info-circle" className="mr-2" />
                    This process can take 10-15 minutes. Please don't close this window.
                  </p>
                </div>
              )}
              {generationStatus?.status === 'completed' && (
                <p className="text-sm text-slate-500">
                  Redirecting to document editor...
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default HolisticGenerationPage;
